import autobind from 'class-autobind';
import debounce from 'lodash/debounce';
import includes from 'lodash/includes';
import throttle from 'lodash/throttle';

import { GRID_ON_OVERLAY_PRESSED } from 'atg/constants/Events';
import observeStore from 'atg/redux/observeStore';
import dialogOwnEvaluation from 'atg/templates/dialog-own-evaluation';
import { isTouch } from 'atg/utils/device';
import EventDispatcher from 'atg/utils/EventDispatcher';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { ui } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

const $ = window.$;

const ensureDecimal = value => {
  let decimalValue = value;
  if (/^[,.-]+$/g.test(value)) {
    decimalValue = '0';
  }
  decimalValue = decimalValue.replaceAll(
    /^(\d{1,3})+([,.]\d{1,2})?$/g,
    function (match, first, second) {
      if (second) {
        return first + second.replace(',', '.');
      }
      return first;
    },
  );
  return decimalValue.replace(',', '.');
};

export default class OwnEvaluationDialog {
  constructor(delegate, grid) {
    autobind(this, OwnEvaluationDialog.prototype);
    StickyTemplate.loadHTML('dialog-own-evaluation', dialogOwnEvaluation);
    this.$dialog = StickyTemplate.jq('dialog-own-evaluation');
    this.grid = grid;
    grid.$container.append(this.$dialog);
    this.$overlay = grid.$overlay;
    this.delegate = delegate;
    this.$dialog.hide();
    this.destroyStoreObserver = observeStore(
      getStore(),
      this.closeDialog.bind(this, false),
      ui.getScale,
    );
    this.setupEvents();
  }

  setupEvents() {
    EventDispatcher.addListener(GRID_ON_OVERLAY_PRESSED, this.onOverlayPressed);
    const throttledResize = throttle(() => {
      if (!isTouch()) {
        this.closeDialog(false);
      } else {
        if (!this.$overlay.hasClass('active')) {
          this.$overlay.addClass('active');
        }
      }
    }, 100);
    $(window).on('resize', throttledResize);
  }

  onOverlayPressed() {
    this.closeDialog(true);
  }

  closeDialog(cleanEvents) {
    if (cleanEvents) {
      EventDispatcher.removeListener(GRID_ON_OVERLAY_PRESSED, this.closeDialog);
      $(window).off('resize');
      this.destroyStoreObserver();
      this.$dialog.remove();
    }
    this.$overlay.removeClass('active');
    this.$dialog.removeAttr('style');
    this.$dialog.children().off('click');
    this.$dialog.hide();
  }

  updateOwnEvaluationRows(value, row, column) {
    const $row = this.delegate.getAPI().betInformation.getRow(row),
      percent =
        value > 10
          ? Number.parseFloat(value).toFixed(0)
          : Number.parseFloat(value).toFixed(2);
    $row.setOwnReductionPercentage(column, percent);
  }

  onSubmitOwnEvaluationDialog(position) {
    const $form = this.$dialog.find('form');
    const formData = $form.formData().values;
    let value;

    /**
     * If formData has only one input, then it means what we use
     * the smaller dialog. In that case we treat it as such.
     * In the case the formData contains more that one input, we will
     * treat it as the bigger dialog and it will go through all inputs.
     */
    if (formData.length === 1) {
      value = formData[0]['ownEvaluationValue'];
      if (!value) {
        return;
      }
      this.updateOwnEvaluationRows(
        ensureDecimal(value),
        position.row,
        position.column,
      );
    } else {
      for (const [dataIndex, formDatum] of formData.entries()) {
        value = formDatum['ownEvaluationValue'];
        if (!value) {
          continue;
        }
        this.updateOwnEvaluationRows(
          ensureDecimal(value),
          position.row,
          dataIndex,
        );
      }
    }
    this.grid.updateAll();
    this.delegate.getAPI().scheduleBetUpdate();
  }

  ownEvaluationDialogSingleInput(totalEl, sum, event) {
    const input = $(event.target).parent().find('input');
    let value = ensureDecimal(input.val() || '0');
    const oldValue = Number.parseFloat(input.data('oldValue'));

    input.data('oldValue', Number.parseFloat(value || 0).toFixed(2));

    if (value) {
      value = sum - oldValue + Number.parseFloat(value);
      value = `${Number.parseFloat(value).toFixed(1)}%`;
    } else {
      value = sum - oldValue;
      value = `${Number.parseFloat(value).toFixed(1)}%`;
    }
    totalEl &&
      totalEl.html(
        Text.get('grid-evaluation-dialog-total', `<strong>${value}</strong>`),
      );
  }

  getOwnEvaluationSumPercentage(ownReductions) {
    return ownReductions.reduce((item, currentItem) => {
      if (currentItem == null) {
        return item;
      }
      return item + currentItem;
    });
  }

  ownEvaluationDialogAdjustLeft() {
    const gridLeft = 0;
    const ownEvaluationDialogLeft = this.$dialog.offset().left;
    let left;

    this.$dialog.removeClass('adjust-arrow');

    if (ownEvaluationDialogLeft < gridLeft) {
      left = 2;
      this.$dialog.addClass('adjust-arrow');
    }

    if (left) {
      this.$dialog.css({
        left: `${left}px`,
        'margin-left': left,
      });
    }
  }

  onValueUpdate(event, $totalEl, ownReductions) {
    if (
      includes([46, 27], event.keyCode) ||
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode >= 35 && event.keyCode <= 39)
    ) {
      return;
    }
    if (
      event.shiftKey ||
      (event.keyCode >= 18 && event.keyCode < 48) ||
      (event.keyCode > 57 && event.keyCode < 96)
    ) {
      event.preventDefault();
    } else {
      const column = $(event.target).data('row');
      const sum = this.getOwnEvaluationSumPercentage(ownReductions);
      const value = event.target.value;
      this.ownEvaluationDialogSingleInput($totalEl, sum, event);
      ownReductions[column] = Number.parseFloat(ensureDecimal(value) || 0);
    }
  }

  showOwnEvaluationDialogFull(position, ownReductions) {
    this.grid.clearOverlayColumn();

    const row = this.delegate.getAPI().betInformation.getRow(position.row),
      cellNumber = row.getOrder().length,
      fragment = document.createDocumentFragment(),
      $evaluationDialog = this.$dialog,
      rowInfo =
        Utils.callDelegate(this.delegate, 'getGridRowInfo', position.row) || {},
      sumPercentage = this.getOwnEvaluationSumPercentage(ownReductions),
      $dialogFull = StickyTemplate.jq('evaluation-dialog-full', {
        '.dialog-evaluation__header h4': rowInfo.title,
        '.dialog-evaluation__total': Text.get(
          'grid-evaluation-dialog-total',
          '0%',
        ),
        '.dialog-evaluation__subheader': Text.get(
          'grid-evaluation-dialog-info',
        ),
      });

    for (let index = 0; index < cellNumber; index++) {
      const $dialogInputItem = StickyTemplate.jq(
        'evaluation-dialog-input-item',
      );
      const input = $dialogInputItem.find('input');
      $dialogInputItem.find('span').text(row.getStartNumberAtPosition(index));
      input.data('row', index);
      input.data('oldValue', ownReductions[index].toFixed(2));
      input.val(ownReductions[index].toFixed(1));
      input.attr('name', `values[${index}][ownEvaluationValue]`);
      fragment.append($dialogInputItem.get(0));
    }

    $dialogFull.find('.dialog-evaluation__inputs').append(fragment);

    $evaluationDialog.html($dialogFull);

    const $closeButton = $dialogFull.find('.dialog-evaluation__close-button'),
      $totalEl = $evaluationDialog.find('.dialog-evaluation__total'),
      inputs = $evaluationDialog.find('.dialog-evaluation__inputs__item input');

    $closeButton.on('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.closeDialog(true);
    });

    $totalEl.html(
      Text.get(
        'grid-evaluation-dialog-total',
        `<strong>${Number.parseFloat(sumPercentage).toFixed(1)}%`,
        +'</strong>',
      ),
    );

    inputs.off().on({
      keydown: event => {
        if (event.keyCode === 13) {
          $dialogFull.submit();
        } else {
          this.onValueUpdate(event, $totalEl, ownReductions);
        }
      },
      keyup: debounce(event => {
        this.onValueUpdate(event, $totalEl, ownReductions);
      }, 600),
    });

    $dialogFull.off().on('submit', event => {
      event.preventDefault();
      event.stopPropagation();
      this.onSubmitOwnEvaluationDialog(position);
      this.closeDialog(this.closeDialog(true));
    });

    const isSmallScreen = window.innerWidth < 768;

    const dialogCenterX =
      (this.grid.getColumnLeft(0) + this.grid.getColumnRight(cellNumber - 1)) /
      2;
    const dialogTop = this.grid.getRowBottom(position.row) + 17;
    const dialogHalfWidth = $evaluationDialog.outerWidth() / 2;
    const dialogBottom =
      (window.innerHeight - $evaluationDialog.outerWidth()) / 2;
    const outer = dialogCenterX - dialogHalfWidth;

    let cssParams = isSmallScreen
      ? {
          bottom: dialogBottom,
          left: 0,
          marginLeft: 8,
          top: 'auto',
        }
      : {
          bottom: 'auto',
          left: dialogCenterX,
          marginLeft: -dialogHalfWidth,
          top: dialogTop,
        };

    if (outer < 0) {
      cssParams = {
        ...cssParams,
        left: 0,
        marginLeft: 8,
      };
    }

    setTimeout(() => $evaluationDialog.css(cssParams), 1);
  }

  showOwnEvaluationDialog(position) {
    this.grid.highlightCell(position);
    const $evaluationDialog = this.$dialog,
      row = this.delegate.getAPI().betInformation.getRow(position.row),
      dialogTop = this.grid.getRowBottom(position.row) + 17,
      ownReductions = Object.assign([], row.getOwnReductionPercentages()),
      sumPercentage = this.getOwnEvaluationSumPercentage(ownReductions),
      $dialogCompact = StickyTemplate.jq('evaluation-dialog-compact', {
        '.dialog-evaluation__header h4': Text.get(
          'grid-evaluation-dialog-compact-header',
        ),
        '.dialog-evaluation__total': Text.get(
          'grid-evaluation-dialog-total',
          '0%',
        ),
        '.dialog-evaluation__subheader': Text.get(
          'grid-evaluation-dialog-compact-subheader',
        ),
        '.dialog-evaluation__openall': Text.get(
          'grid-evaluation-dialog-compact-openall',
        ),
      });

    $evaluationDialog.show();
    $evaluationDialog.html($dialogCompact);

    const $closeButton = $dialogCompact.find(
        '.dialog-evaluation__close-button',
      ),
      $openAllButton = $dialogCompact.find('.dialog-evaluation__openall'),
      $totalEl = $evaluationDialog.find('.dialog-evaluation__total'),
      inputs = $evaluationDialog.find('.dialog-evaluation__inputs input');

    $closeButton.on('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.closeDialog(true);
    });

    $openAllButton.on('click', () => {
      $openAllButton.off('click');
      this.$dialog.children().off('click');
      this.$dialog.empty();
      $evaluationDialog.addClass('full');
      this.showOwnEvaluationDialogFull(position, ownReductions);
    });

    $totalEl.html(
      Text.get(
        'grid-evaluation-dialog-total',
        `<strong>${Number.parseFloat(sumPercentage).toFixed(1)}%`,
        +'</strong>',
      ),
    );

    inputs.data('oldValue', ownReductions[position.column].toFixed(2));
    inputs.val(ownReductions[position.column].toFixed(1));
    inputs.data('row', position.column);

    inputs.off().on({
      keydown: event => {
        if (event.keyCode === 13) {
          $dialogCompact.submit();
        } else {
          this.onValueUpdate(event, $totalEl, ownReductions);
        }
      },
      keyup: debounce(event => {
        this.onValueUpdate(event, $totalEl, ownReductions);
      }, 600),
    });

    $dialogCompact.off().on('submit', event => {
      event.preventDefault();
      event.stopPropagation();
      this.onSubmitOwnEvaluationDialog(position);
      this.closeDialog(true);
    });

    const dialogCenterX =
      (this.grid.getColumnLeft(position.column) +
        this.grid.getColumnRight(position.column)) /
      2;

    setTimeout(() => {
      $evaluationDialog.css({
        left: `${dialogCenterX}px`,
        top: `${dialogTop}px`,
        'margin-left': -$evaluationDialog.outerWidth() / 2,
      });
      this.ownEvaluationDialogAdjustLeft();
    }, 1);
  }
}
